import React, { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import classNames from "classnames";
import type {
    DefaultValues,
    FieldPath,
    FieldPathValue,
    FieldValues,
    KeepStateOptions,
    Resolver,
    SetValueConfig,
    SubmitErrorHandler,
    SubmitHandler,
    UseFormProps,
    UseFormReturn,
} from "react-hook-form";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import { parseClassName } from "@src/shared/utils/parseClassName";
import type { ClassNameMap } from "@src/shared/utils/parseClassName";
import styles from "./Form.module.scss";
import { FormConfigProvider } from "./FormContext";
import { FormField } from "./FormField";
import type { FormSchemaConfig } from "./formSchema";
import { FORM_ROOT_CLASS } from "./unsavedChanges";
/**
 * Спецификация колонок для CSS Grid.
 *
 * Определяет, как будут распределены колонки в сетке:
 * - Число → равномерные колонки (`2` → `repeat(2, 1fr)`)
 * - Строка → прямая запись для `grid-template-columns` (`"1fr 2fr"`)
 * - Массив → колонки через пробел (`["120px", "1fr", "auto"]`)
 *
 * @example
 * columns={2} → grid-template-columns: repeat(2, 1fr)
 * columns="1fr 2fr" → grid-template-columns: 1fr 2fr
 * columns={['80px', '1fr']} → grid-template-columns: 80px 1fr
 */
type ColumnSpec = number | string | (string | number)[];
/**
 * Преобразует {@link ColumnSpec} в строку, пригодную для `grid-template-columns`.
 *
 * @param columns спецификация колонок
 * @returns строка для CSS `grid-template-columns` или `undefined`, если не задано
 */
const getColumnTemplate = (columns: ColumnSpec): string | undefined => {
    if (columns == null) return undefined;
    if (typeof columns === "number") {
        return `repeat(${columns}, 1fr)`;
    }
    if (typeof columns === "string") {
        return columns;
    }
    if (Array.isArray(columns)) {
        return columns.join(" ");
    }
    return "1fr";
};
/**
 * Сливает дефолты {@link FormProps.markDirtyOnSetValue}/{@link FormProps.validateOnSetValue} \
 * с опциями вызова `setValue` react-hook-form. \
 * Явные ключи в `options` перекрывают дефолты (например, `shouldValidate: false` отключает валидацию для одного вызова).
 *
 * @param markDirtyOnSetValue включены ли по умолчанию `shouldDirty` и `shouldTouch`
 * @param validateOnSetValue включён ли по умолчанию `shouldValidate`
 * @param options опции вызова `setValue` от вызывающего кода
 * @returns объект для третьего аргумента `setValue` или `undefined`, если итог пустой
 */
function mergeSetValueOptions(
    markDirtyOnSetValue: boolean,
    validateOnSetValue: boolean,
    options?: SetValueConfig,
): SetValueConfig | undefined {
    const defaults: SetValueConfig = {
        ...(markDirtyOnSetValue ? { shouldDirty: true, shouldTouch: true } : {}),
        ...(validateOnSetValue ? { shouldValidate: true } : {}),
    };
    const merged: SetValueConfig = { ...defaults, ...options };
    return Object.keys(merged).length > 0 ? merged : undefined;
}

/**
 * После `mousedown` дожидается нативной фокусировки браузера и проверяет,
 * остался ли фокус внутри контейнера формы.
 * @param root корневой контейнер формы
 * @returns `true`, если нативный фокус уже внутри формы и перехватывать его не нужно
 */
function hasFocusInsideRootAfterNativeMouseFocus(root: HTMLDivElement): Promise<boolean> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            const active = document.activeElement;
            resolve(active != null && active !== document.body && root.contains(active));
        });
    });
}
/**
 * Событие `change` нативных контролов со строковым `event.target.value` (`input`, `textarea`, `select`).
 *
 * @see {@link getInputFieldProps}
 */
export type FormFieldNativeStringChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

/**
 * Пропсы для нативных контролов со строкой в `target.value`.
 * Собираются через `form.field(name).getInputProps()` или `getPropsWithLabel()`.
 *
 * `onChange` и `onBlur` дергают `setValue` и `trigger` с дефолтами {@link FormProps.markDirtyOnSetValue}.
 *
 * @template TFieldValues тип значений формы
 * @template TName путь к полю в `TFieldValues`
 */
export type FormFieldInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
    /** Текущее значение поля из состояния формы. */
    value: FieldPathValue<TFieldValues, TName>;
    /**
     * Запись в форму из `event.target.value`.
     * @param e событие `change` нативного контрола
     */
    onChange: (e: FormFieldNativeStringChangeEvent) => void;
    /** Триггер валидации поля (`react-hook-form`). */
    onBlur: () => void;
    /** Признак ошибки поля для UI (текст ошибки показывает {@link FormField}). */
    error: boolean;
};

/**
 * Пропсы для контролов «значение целиком» (`Transfer`, кастомные виджеты).
 * При необходимости переопредели `onChange` поверх спреда: `onChange={(v, info) => { …; p.onChange(v); }}`.
 *
 * @template TFieldValues тип значений формы
 * @template TName путь к полю в `TFieldValues`
 */
export type FormFieldControlledProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
    /** Текущее значение поля. */
    value: FieldPathValue<TFieldValues, TName>;
    /**
     * Запись нового значения в форму.
     * @param value значение для `setValue` по имени поля
     */
    onChange: (value: FieldPathValue<TFieldValues, TName>) => void;
    /** Триггер валидации поля. */
    onBlur: () => void;
    /** Признак ошибки поля для UI. */
    error: boolean;
};

/**
 * Собирает пропсы нативного контрола из `UseFormReturn`.
 * @param form API `react-hook-form` (как в render-children `Form`)
 * @param name имя поля
 * @returns объект для спреда в `input` / `textarea` / `select`
 */
function getInputFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    form: UseFormReturn<TFieldValues>,
    name: TName,
): FormFieldInputProps<TFieldValues, TName> {
    const err = form.getFieldState(name).error;
    return {
        value: form.watch(name),
        onChange: (e: FormFieldNativeStringChangeEvent) =>
            form.setValue(name, e.target.value as FieldPathValue<TFieldValues, TName>),
        onBlur: () => void form.trigger(name),
        error: err != null,
    };
}

/**
 * Собирает пропсы контрола с `onChange(значение)` из `UseFormReturn`.
 * @param form API `react-hook-form`
 * @param name имя поля
 * @returns объект для спреда; при другой сигнатуре `onChange` у контрола оберни вызов вручную
 */
function getControlledFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    form: UseFormReturn<TFieldValues>,
    name: TName,
): FormFieldControlledProps<TFieldValues, TName> {
    const err = form.getFieldState(name).error;
    return {
        value: form.watch(name),
        onChange: (next: FieldPathValue<TFieldValues, TName>) => form.setValue(name, next),
        onBlur: () => void form.trigger(name),
        error: err != null,
    };
}

/**
 * Как {@link FormFieldInputProps}, плюс подпись из `schemaConfig` (если задана).
 *
 * @template TFieldValues тип значений формы
 * @template TName путь к полю в `TFieldValues`
 */
export type FormFieldPropsWithLabel<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>,
> = FormFieldInputProps<TFieldValues, TName> & {
    /** Подпись из `schemaConfig.fields[name].label`. */
    label?: string;
    /** Признак обязательного поля из `schemaConfig.fields[name].validation.required`. */
    required?: boolean;
};

/**
 * Как {@link FormFieldControlledProps}, плюс подпись из `schemaConfig` (если задана).
 *
 * @template TFieldValues тип значений формы
 * @template TName путь к полю в `TFieldValues`
 */
export type FormFieldControlledPropsWithLabel<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>,
> = FormFieldControlledProps<TFieldValues, TName> & {
    /** Подпись из `schemaConfig.fields[name].label`. */
    label?: string;
    /** Признак обязательного поля из `schemaConfig.fields[name].validation.required`. */
    required?: boolean;
};

/**
 * Fluent-хелпер для одного поля формы.
 * Возвращается из `form.field(name)` и собирает пропсы для контрола.
 *
 * @template TFieldValues тип значений формы
 * @template TName имя поля в `TFieldValues`
 */
export type FormFieldBinding<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
    /**
     * Объект для спреда в нативный контрол (`input`, `textarea`, `select`). \
     * Поля: `value` (текущее значение поля), `onChange` (пишет `event.target.value` в форму, см. {@link FormFieldNativeStringChangeEvent}), `onBlur` (триггер валидации поля), `error` (флаг ошибки для UI; текст ошибки показывает {@link FormField}). \
     * @see {@link FormFieldInputProps} полный тип
     */
    getInputProps: () => FormFieldInputProps<TFieldValues, TName>;
    /**
     * Объект для спреда в контролы с собственной сигнатурой `onChange` (например `Transfer`). \
     * Поля: `value`, `onChange(next)` (пишет `next` в форму одним `setValue`), `onBlur`, `error` (как у `getInputProps`). \
     * Если у виджета больше аргументов в `onChange`, оберни: `onChange={(v, info) => { …; p.onChange(v); }}`. \
     * @see {@link FormFieldControlledProps} полный тип
     */
    getControlledProps: () => FormFieldControlledProps<TFieldValues, TName>;
    /**
     * То же, что `getControlledProps()`, плюс `label`/`required` из `schemaConfig`.
     * Удобно для контролов с `onChange(value)` (Select/Transfer/кастомные виджеты).
     */
    getControlledPropsWithLabel: () => FormFieldControlledPropsWithLabel<TFieldValues, TName>;
    /**
     * То же, что `getInputProps()`, плюс опциональный `label` из `schemaConfig.fields[name].label` для единого источника подписи. \
     * Итого ключи: `value`, `onChange`, `onBlur`, `error`, `label?`. \
     * @see {@link FormFieldPropsWithLabel} полный тип
     */
    getPropsWithLabel: () => FormFieldPropsWithLabel<TFieldValues, TName>;
    /**
     * Только строка подписи из схемы, без `value` / `onChange` и т.д.
     * @returns `label` из `schemaConfig` или `undefined`, если для поля подпись не задана
     */
    getLabel: () => string | undefined;
};

/**
 * Читает `label` для поля из `schemaConfig.fields`.
 * @param schemaConfig конфигурация схемы формы или `undefined`
 * @param name имя поля
 * @returns текст подписи или `undefined`
 */
function getSchemaFieldLabel<TFieldValues extends FieldValues>(
    schemaConfig: FormSchemaConfig | undefined,
    name: FieldPath<TFieldValues>,
): string | undefined {
    return schemaConfig?.fields != null && typeof name === "string" && name in schemaConfig.fields
        ? (schemaConfig.fields as Record<string, { label?: string }>)[name]?.label
        : undefined;
}

/**
 * Нормализует правило `required` из `schemaConfig` к булевому флагу.
 * Поддерживает формы `true` и `{ value: true }`.
 * @param value значение правила `required` из схемы
 * @returns `true`, если поле должно считаться обязательным
 */
function isRequiredValidationRule(value: unknown): boolean {
    if (value === true) return true;
    if (value != null && typeof value === "object" && "value" in value) {
        return (value as { value?: unknown }).value === true;
    }
    return false;
}

/**
 * Возвращает метаданные поля (`label`, `required`) из `schemaConfig`.
 * @param schemaConfig конфигурация схемы формы или `undefined`
 * @param name имя поля
 * @returns объект с метаданными поля; пустой объект, если поле не найдено
 */
function getSchemaFieldMeta<TFieldValues extends FieldValues>(
    schemaConfig: FormSchemaConfig | undefined,
    name: FieldPath<TFieldValues>,
): { label?: string; required?: boolean } {
    if (schemaConfig?.fields == null || typeof name !== "string" || !(name in schemaConfig.fields)) {
        return {};
    }
    const field = (schemaConfig.fields as Record<string, { label?: string; validation?: { required?: unknown } }>)[name];
    return {
        label: field.label,
        required: isRequiredValidationRule(field.validation?.required),
    };
}
/**
 * API формы в render-children: тот же {@link UseFormReturn}, что и в `FormProvider`, плюс хелперы. \
 * Метод `setValue` обёрнут: дефолты для `isDirty` задаются пропом {@link FormProps.markDirtyOnSetValue}.
 *
 * @example
 * ```tsx
 * <Form initialValues={{ sign: "", name: "" }}>
 *   {(form) => (
 *     <Form.Field name="sign">
 *       <Input label="Код" {...form.field("sign").getInputProps()} />
 *     </Form.Field>
 *   )}
 * </Form>
 * ```
 *
 * Fluent-вариант с тем же результатом:
 * ```tsx
 * <Form.Field name="sign">
 *   <Input {...form.field("sign").getPropsWithLabel()} />
 * </Form.Field>
 * ```
 *
 * @template TFieldValues тип значений формы
 */
export type FormRenderProps<TFieldValues extends FieldValues> = Omit<UseFormReturn<TFieldValues>, "register"> & {
    /**
     * Fluent-API поля: `getInputProps`, `getControlledProps`, `getPropsWithLabel`, `getLabel`.
     * @param name имя поля в `TFieldValues`
     *
     * @example
     * ```tsx
     * <Form.Field name="code">
     *   <Input {...form.field("code").getPropsWithLabel()} />
     * </Form.Field>
     * ```
     */
    field: <TName extends FieldPath<TFieldValues>>(name: TName) => FormFieldBinding<TFieldValues, TName>;
    /** Корневой `div` формы: фокус, хоткеи и доступ снаружи компонента. */
    rootRef: React.RefObject<HTMLDivElement | null>;
};
/**
 * Обёртка над `handleSubmit` с `Promise<boolean>` и сбросом `isDirty` после успеха (путь ref и Ctrl+Enter).
 *
 * @template TFieldValues тип значений формы
 * @param onValid колбэк при успешной валидации
 * @param onInvalid опционально при ошибках валидации
 * @returns функция отправки (как у RHF), резолвится в `true`, если валидация и `onValid` прошли без ошибки
 *
 * Если `onValid` возвращает `Promise` (async-сохранение), она **ожидается** внутри конвейера submit; в это время у react-hook-form
 * `formState.isSubmitting === true` (см. {@link FormHandle.subscribeSubmitting}, {@link FormProps.onSubmit}).
 */
export type FormHandleSubmit<TFieldValues extends FieldValues = FieldValues> = (
    onValid: SubmitHandler<TFieldValues>,
    onInvalid?: SubmitErrorHandler<TFieldValues>,
) => (e?: React.BaseSyntheticEvent) => Promise<boolean>;

/**
 * Императивный API формы (ref). Позволяет вызывать submit и другие методы снаружи,
 * например из футера overlay-окна, когда кнопка «Сохранить» не находится внутри формы в DOM.
 *
 * @template TFieldValues тип значений формы
 *
 * @example
 * ```tsx
 * const formRef = useRef<FormHandle>(null);
 * <WindowFrame footer={<FormFooter onConfirm={() => void formRef.current?.handleSubmit(onSave)()} />}>
 *   <Form ref={formRef} initialValues={...} onSubmit={onSave}>
 *     {(form) => <Tabs>...</Tabs>}
 *   </Form>
 * </WindowFrame>
 * ```
 */
export interface FormHandle<TFieldValues extends FieldValues = FieldValues> {
    /**
     * Валидация и вызов `onValid` при успехе. Возвращает `Promise<boolean>`: `true` — валидация прошла и колбэк
     * завершился без ошибки (после успеха форма делает `reset` текущих значений, `isDirty` сбрасывается).
     *
     * Колбэк `onValid` может быть async: пока его `Promise` не завершился, {@link getIsSubmitting} / {@link subscribeSubmitting}
     * отражают фазу отправки (удобно для `confirmLoading` во внешнем футере).
     */
    handleSubmit: FormHandleSubmit<TFieldValues>;
    /**
     * Текущий флаг несохранённых изменений (`formState.isDirty` react-hook-form).
     * Удобно снаружи формы (футер окна, `ref`).
     */
    getIsDirty: () => boolean;
    /**
     * Подписка на смену `formState.isDirty` (в т.ч. после успешного submit через ref).
     * @param listener вызывается при изменении флага и сразу после подписки с текущим значением
     * @returns функция отписки
     */
    subscribeDirty: (listener: (isDirty: boolean) => void) => () => void;
    /**
     * Текущий флаг `formState.isSubmitting` react-hook-form: `true` с момента успешной валидации до завершения
     * async-колбэка сабмита (в т.ч. {@link FormProps.onSubmit} и колбэка в {@link FormHandle.handleSubmit}).
     */
    getIsSubmitting: () => boolean;
    /**
     * Подписка на смену `formState.isSubmitting` (кнопка ОК модалки, Ctrl+Enter, императивный `handleSubmit`).
     * @param listener вызывается при изменении флага и сразу после подписки с текущим значением
     * @returns функция отписки
     */
    subscribeSubmitting: (listener: (isSubmitting: boolean) => void) => () => void;
    /**
     * Сбрасывает форму к переданным значениям (или к `defaultValues`, если не переданы).
     * Пробрасывает `reset` из `react-hook-form`.
     * @param values значения, в которые нужно сбросить форму
     * @param keepStateOptions флаги сохранения частей состояния (`errors`, `dirty`, `submitCount` и т.д.)
     */
    reset: (values?: TFieldValues | DefaultValues<TFieldValues>, keepStateOptions?: KeepStateOptions) => void;
}
/**
 * Пропсы компонента {@link Form}.
 *
 * При передаче `initialValues`, `zodSchema` или `onSubmit` форма работает в режиме с состоянием
 * (react-hook-form под капотом): доступны валидация, submit по Ctrl+Enter, dirty-трекинг.
 * Без этих пропсов форма используется как layout (сетка секций и строк), но под капотом всё равно
 * создаётся useForm, так что доступны FormProvider, Form.Field и хоткей Ctrl+Enter (если включён).
 *
 * Поддержка ref: через ref доступен {@link FormHandle} (`handleSubmit` возвращает `Promise<boolean>`).
 *
 * **Async `onSubmit`:** колбэк может быть `async` или возвращать `Promise`. Под капотом выполняется `await` на этом промисе после
 * успешной валидации; react-hook-form выставляет `isSubmitting`, к которому можно подписаться через ref
 * ({@link FormHandle.subscribeSubmitting}).
 *
 * @template TFieldValues тип значений полей формы
 */
interface FormProps<TFieldValues extends FieldValues = FieldValues> {
    /**
     * Дополнительный CSS-класс для корневого элемента.
     */
    className?: string;
    /**
     * Содержимое формы.
     *
     * Два варианта:
     * - `ReactNode` — обычные дочерние элементы (секции, строки, поля). field.value будет unknown.
     * - `(form: FormRenderProps<TFieldValues>) => ReactNode` — **render-функция**: получает
     *   объект формы с fluent-хелпером `form.field("name").getInputProps()` / `getControlledProps()` для пропсов полей.
     *
     * @example
     * ```tsx
     * <Form initialValues={{ code: "", count: 0 }}>
     *   {(form) => (
     *     <Form.Field name="code">
     *       <Input {...form.field("code").getInputProps()} />
     *     </Form.Field>
     *   )}
     * </Form>
     * ```
     */
    children: React.ReactNode | ((form: FormRenderProps<TFieldValues>) => React.ReactNode);
    /**
     * Спецификация колонок для макросетки формы.
     *
     * Управляет количеством и шириной колонок на верхнем уровне формы.
     *
     * @default 1
     * @see {@link ColumnSpec}
     */
    columns?: ColumnSpec;
    /**
     * Начальные значения полей. Тип формы выводится из этого объекта:
     * `{ code: "", count: 0 }` → `TFieldValues = { code: string; count: number }`.
     *
     * При задании вместе с {@link onSubmit} форма переходит в режим с состоянием. \
     * Значение используется как initial snapshot для `react-hook-form` при маунте и не должно меняться после первого рендера.
     *
     * @warning Изменение `initialValues` после маунта не обновляет текущее состояние формы автоматически. \
     * Для асинхронных данных используйте `reset(...)`, remount по `key` или рендерите форму только после загрузки данных.
     *
     * TODO(forms-api): предусмотреть штатный сценарий «серверные данные меняются, пока форма открыта»
     * (мутации в том же окне, query-обновления). Сейчас экраны решают это локально через
     * `FormHandle.reset(..., { keepDirtyValues: true })` — см. `StockPlacementContentForm`.
     * При переработке форм и вынесении в отдельный пакет спроектировать единый API поверх
     * нативного `useForm({ values, resetOptions })` из react-hook-form, а не третий value-проп
     * рядом с `initialValues`/`seedValues`: у mature-библиотек таких пар максимум две
     * (RHF `defaultValues`+`values`, Formik `initialValues`+`enableReinitialize`).
     */
    initialValues?: TFieldValues;
    /**
     * Готовый resolver для `react-hook-form` (например из `defineFormConfig`).
     */
    resolver?: Resolver<TFieldValues>;
    /**
     * Схема формы: декларация полей и метаданных (label, required).
     */
    schemaConfig?: FormSchemaConfig;
    /**
     * Колбэк при успешной валидации и отправке формы. Типизирован по `TFieldValues`.
     *
     * Варианты возврата:
     * - **Синхронный возврат (`void`)** — колбэк завершается сразу; `isSubmitting` у RHF почти незаметен.
     * - **Асинхронный возврат (`Promise<void>`)** — пока промис в состоянии pending, `formState.isSubmitting === true`. Это даёт:
     *   - блокировку повторного submit и индикацию загрузки, если UI подписан на {@link FormHandle.subscribeSubmitting}
     *   - тот же эффект при отправке через Ctrl+Enter (тот же конвейер, что и у {@link FormHandle.handleSubmit}).
     *
     * Внутри {@link Form} после валидации вызывается `await Promise.resolve(onSubmit(data))`; отклонение промиса не даёт
     * считать submit успешным (успешный путь дополнительно сбрасывает `isDirty` через `reset`). Ошибка выполнения при этом
     * дополнительно передаётся в {@link onSubmitExecutionError}, если проп задан; иначе сбой только отражается в `false` из
     * {@link FormHandle.handleSubmit} и отсутствии сброса `isDirty`, без глобальных уведомлений.
     *
     * @param data провалидированные данные
     */
    onSubmit?: (data: TFieldValues) => void | Promise<void>;
    /**
     * Вызывается при ошибке **выполнения** колбэка валидного submit (синхронный `throw` или отклонение `Promise` у
     * {@link onSubmit} или у колбэка в {@link FormHandle.handleSubmit}), а не при ошибках валидации полей (их обрабатывает
     * {@link onError}).
     *
     * @param error то, что было брошено или с чем отклонился промис
     */
    onSubmitExecutionError?: (error: unknown) => void;
    /**
     * Колбэк при ошибке валидации на submit (как у react-hook-form {@link SubmitErrorHandler}).
     * Вызывается при любом сабмите с провалом валидации: {@link FormHandle.handleSubmit} через ref,
     * Ctrl+Enter и т.п. Дополнительно можно передать свой `onInvalid` вторым аргументом ref — он вызовется после этого пропа.
     *
     * Для плоского списка `{ path, message }` см. {@link flattenFieldErrors} (`./flattenFieldErrors.ts`).
     * @param errors дерево ошибок по полям (как `FieldErrors` в react-hook-form)
     */
    onError?: SubmitErrorHandler<TFieldValues>;
    /**
     * Отправлять форму по Ctrl+Enter.
     * @default true
     */
    submitOnCtrlEnter?: boolean;
    /**
     * Префикс для автоматической генерации `data-testid` у полей (e2e).
     * Например, `testId="warehouse"` даёт полю `name="code"` значение `warehouse-field-code`.
     */
    testId?: string;
    /**
     * Оборачивает `handleSubmit` из `useForm` (логирование, отладка, телеметрия).
     * @param handleSubmit исходный `handleSubmit` react-hook-form
     * @returns замена с той же сигнатурой для `FormProvider`
     */
    handleSubmitEnhancer?: (
        handleSubmit: UseFormReturn<TFieldValues>["handleSubmit"],
    ) => UseFormReturn<TFieldValues>["handleSubmit"];
    /**
     * При `true` каждый вызов `setValue` из этой формы (включая {@link getInputFieldProps} и {@link getControlledFieldProps}) по умолчанию получает \
     * `shouldDirty: true` и `shouldTouch: true`, чтобы react-hook-form обновлял `isDirty` (иначе голый `setValue` этого не делает). \
     * В третьем аргументе `setValue` можно явно передать `shouldDirty: false` и т.д. — значения перекроют дефолт.
     * @default true
     */
    markDirtyOnSetValue?: boolean;
    /**
     * При `true` каждый вызов `setValue` из этой формы (включая `form.field(...).getInputProps()` и `getControlledProps()`) \
     * по умолчанию получает `shouldValidate: true`, чтобы ошибки поля обновлялись сразу после изменения значения.
     *
     * Это удобно для контролов без надёжного `blur`-цикла (например, `Select`), когда после submit нужно снять ошибку сразу на `onChange`.
     * Локально поведение можно переопределить третьим аргументом `setValue`.
     * @default true
     */
    validateOnSetValue?: boolean;
    /**
     * Вызывается при изменении `formState.isDirty` (react-hook-form).
     * Удобно для синхронизации черновика с родителем (модалка).
     * @param isDirty актуальное значение флага несохранённых изменений
     */
    onDirtyChange?: (isDirty: boolean) => void;
    /**
     * Включает фокус корневого контейнера формы по клику в неинтерактивную область. \
     * Удобно для сценариев с `:focus-within`, когда нужно подсветить форму даже при клике «в пустоту».
     * @default false
     */
    focusOnContainerClick?: boolean;
    /**
     * Принудительно создаёт собственный `FormProvider`, даже если `Form` вложен в другую форму без своих state-пропсов.
     * По умолчанию вложенный `Form` без `initialValues` / `resolver` / `zodSchema` / `schemaConfig` / `onSubmit` и с `children` не как функция
     * рендерится только как layout (сетка), без второго провайдера — чтобы `Form.Field` не терял контекст внешней формы.
     * @default false
     */
    standalone?: boolean;
}

/**
 * Внутренний контекст: текущий узел находится внутри `Form` с собственным `FormProvider`.
 * Нужен для авто-режима layout-only у вложенного `Form` без state.
 */
const FormNestingContext = React.createContext(false);
/**
 * Форма с собственным `useForm` / `FormProvider`, сетка и хоткей Ctrl+Enter.
 * Оборачивает дерево в {@link FormNestingContext}, чтобы вложенные `Form` могли перейти в layout-only.
 *
 * @template TFieldValues тип значений формы
 */
const FormStateful = React.forwardRef(function FormStatefulInner<TFieldValues extends FieldValues = FieldValues>(
    props: FormProps<TFieldValues>,
    ref: React.Ref<FormHandle<TFieldValues>>,
) {
    const {
        className,
        children,
        columns = 1,
        initialValues,
        resolver: userResolver,
        schemaConfig,
        onSubmit,
        onSubmitExecutionError,
        onError,
        submitOnCtrlEnter = true,
        testId,
        handleSubmitEnhancer,
        markDirtyOnSetValue = true,
        validateOnSetValue = true,
        onDirtyChange,
        focusOnContainerClick = false,
    } = props;
    const gridTemplateColumns = getColumnTemplate(columns);
    const formOptions = useMemo<UseFormProps<TFieldValues>>(() => {
        const resolver = userResolver as UseFormProps<TFieldValues>["resolver"];
        return {
            defaultValues: (initialValues ?? {}) as DefaultValues<TFieldValues>,
            resolver,
            mode: "onSubmit",
        };
    }, [initialValues, userResolver]);
    const methods = useForm<TFieldValues>(formOptions);
    const methodsRef = useRef(methods);
    methodsRef.current = methods;
    const formRootRef = useRef<HTMLDivElement>(null);
    const dirtySubscribersRef = useRef(new Set<(dirty: boolean) => void>());
    const submittingSubscribersRef = useRef(new Set<(isSubmitting: boolean) => void>());
    /**
     * Явная подписка формы на `useFormState` вместо `void methods.formState.*`: \
     * это гарантирует повторный рендер `Form` при изменении `errors`, чтобы `children(form)` и `form.field(...).getInputProps()` \
     * пересчитывались с актуальным `error` без «случайных» внешних ререндеров (например, HMR/отладка).
     *
     * Trade-off: подписка на весь `errors` может давать больше ререндеров на больших формах.
     * Оптимизация: выносить тяжёлые блоки из `children(form)` в `React.memo`, а для hot-path полей использовать
     * точечные подписки (`useFormState({ name })` / `useController`) ближе к самому полю.
     */
    const { isDirty, errors, isSubmitting, dirtyFields } = useFormState({ control: methods.control });
    void errors; // подписка на proxy
    /** Без активной подписки на `dirtyFields` у потребителей ломается `reset(..., { keepDirtyValues: true })`. */
    void dirtyFields;
    useEffect(() => {
        onDirtyChange?.(isDirty);
        dirtySubscribersRef.current.forEach((listener) => {
            listener(isDirty);
        });
    }, [isDirty, onDirtyChange]);
    useEffect(() => {
        submittingSubscribersRef.current.forEach((listener) => {
            listener(isSubmitting);
        });
    }, [isSubmitting]);
    const wrappedHandleSubmit = useMemo(() => {
        const base = methods.handleSubmit;
        return handleSubmitEnhancer != null ? handleSubmitEnhancer(base) : base;
    }, [methods.handleSubmit, handleSubmitEnhancer]);
    const subscribeDirty = useCallback((listener: (dirty: boolean) => void) => {
        const set = dirtySubscribersRef.current;
        set.add(listener);
        listener(methodsRef.current.formState.isDirty);
        return () => {
            set.delete(listener);
        };
    }, []);
    const subscribeSubmitting = useCallback((listener: (isSubmittingFlag: boolean) => void) => {
        const set = submittingSubscribersRef.current;
        set.add(listener);
        listener(methodsRef.current.formState.isSubmitting);
        return () => {
            set.delete(listener);
        };
    }, []);
    const handleSubmitWithResult = useMemo<FormHandleSubmit<TFieldValues>>(
        () => (onValid, onInvalid) => async (e) => {
            let success = false;
            const run = wrappedHandleSubmit(
                async (data) => {
                    try {
                        await Promise.resolve(onValid(data));
                        success = true;
                    } catch (error) {
                        success = false;
                        onSubmitExecutionError?.(error);
                    }
                },
                (errors) => {
                    onError?.(errors);
                    onInvalid?.(errors);
                },
            );
            try {
                await run(e);
            } catch (error) {
                success = false;
                onSubmitExecutionError?.(error);
            }
            if (success) {
                const m = methodsRef.current;
                // Keep submit metadata for debug while still clearing dirty state.
                m.reset(m.getValues(), {
                    keepSubmitCount: true,
                    keepIsSubmitted: true,
                    keepIsSubmitSuccessful: true,
                });
            }
            return success;
        },
        [wrappedHandleSubmit, onError, onSubmitExecutionError],
    );
    const setValue = useCallback<UseFormReturn<TFieldValues>["setValue"]>(
        (name, value, options) =>
            methodsRef.current.setValue(
                name,
                value,
                mergeSetValueOptions(markDirtyOnSetValue, validateOnSetValue, options) as Parameters<
                    UseFormReturn<TFieldValues>["setValue"]
                >[2],
            ),
        [markDirtyOnSetValue, validateOnSetValue],
    );
    const handleSubmitValid = useCallback((data: TFieldValues) => Promise.resolve(onSubmit?.(data)), [onSubmit]);
    const formConfig = useMemo(() => (testId != null ? { testId } : {}), [testId]);
    const handleRootMouseDown = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!focusOnContainerClick) return;
            if (event.button !== 0 || event.defaultPrevented) return;
            const root = formRootRef.current;
            if (root == null) return;
            void hasFocusInsideRootAfterNativeMouseFocus(root).then((hasNativeFocusInside) => {
                if (!hasNativeFocusInside) {
                    root.focus();
                }
            });
        },
        [focusOnContainerClick],
    );
    const handleRootKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!submitOnCtrlEnter) return;
            if (!(event.ctrlKey || event.metaKey) || event.key !== "Enter") return;
            const root = formRootRef.current;
            const active = document.activeElement;
            if (root != null && active != null && root.contains(active)) {
                event.preventDefault();
                void handleSubmitWithResult(handleSubmitValid)();
            }
        },
        [submitOnCtrlEnter, handleSubmitWithResult, handleSubmitValid],
    );
    useImperativeHandle(
        ref,
        () => ({
            handleSubmit: handleSubmitWithResult,
            getIsDirty: () => methodsRef.current.formState.isDirty,
            subscribeDirty,
            getIsSubmitting: () => methodsRef.current.formState.isSubmitting,
            subscribeSubmitting,
            reset: (values, keepStateOptions) => methodsRef.current.reset(values, keepStateOptions),
        }),
        [handleSubmitWithResult, subscribeDirty, subscribeSubmitting],
    );
    /** В контекст и render-props: обёрнутые {@link setValue} и {@link wrappedHandleSubmit} (сигнатура RHF). Императивный {@link FormHandle.handleSubmit} и Ctrl+Enter — через {@link handleSubmitWithResult}. */
    const formProviderApi = useMemo(
        () => ({ ...methods, setValue, handleSubmit: wrappedHandleSubmit }),
        [methods, setValue, wrappedHandleSubmit],
    );
    const formWithHelpers = useMemo<FormRenderProps<TFieldValues>>(() => {
        const { register: _register, ...formProviderApiWithoutRegister } = formProviderApi;
        void _register;
        return {
            ...formProviderApiWithoutRegister,
            field: (name) => ({
                getInputProps: () => getInputFieldProps(formProviderApi, name),
                getControlledProps: () => getControlledFieldProps(formProviderApi, name),
                getControlledPropsWithLabel: () => {
                    const base = getControlledFieldProps(formProviderApi, name);
                    const meta = getSchemaFieldMeta(schemaConfig, name);
                    return { ...base, ...meta };
                },
                getPropsWithLabel: () => {
                    const base = getInputFieldProps(formProviderApi, name);
                    const meta = getSchemaFieldMeta(schemaConfig, name);
                    return { ...base, ...meta };
                },
                getLabel: () => getSchemaFieldLabel(schemaConfig, name),
            }),
            rootRef: formRootRef,
        };
    }, [formProviderApi, schemaConfig]);
    const content = typeof children === "function" ? children(formWithHelpers) : children;
    return (
        <FormNestingContext.Provider value={true}>
            <FormProvider {...formProviderApi}>
                <FormConfigProvider value={formConfig}>
                    <div
                        ref={formRootRef}
                        className={classNames(styles.formRoot, FORM_ROOT_CLASS, className)}
                        style={{ gridTemplateColumns }}
                        data-form-root="true"
                        data-form-dirty={String(isDirty)}
                        tabIndex={focusOnContainerClick ? -1 : undefined}
                        onMouseDown={focusOnContainerClick ? handleRootMouseDown : undefined}
                        onKeyDown={handleRootKeyDown}
                    >
                        {content}
                    </div>
                </FormConfigProvider>
            </FormProvider>
        </FormNestingContext.Provider>
    );
}) as <TFieldValues extends FieldValues = FieldValues>(
    props: FormProps<TFieldValues> & { ref?: React.Ref<FormHandle<TFieldValues>> },
) => React.ReactElement | null;

/**
 * Корневой `Form`: при вложении без state рендерит только сетку (см. {@link FormProps.standalone}), иначе делегирует {@link FormStateful}.
 */
const FormWithState = React.forwardRef(function FormWithStateInner<TFieldValues extends FieldValues = FieldValues>(
    props: FormProps<TFieldValues>,
    ref: React.Ref<FormHandle<TFieldValues>>,
) {
    const isInsideForm = useContext(FormNestingContext);
    const {
        children,
        className,
        columns = 1,
        initialValues,
        resolver: userResolver,
        schemaConfig,
        onSubmit,
        standalone = false,
    } = props;
    const gridTemplateColumns = getColumnTemplate(columns);
    const hasOwnState = initialValues !== undefined || userResolver != null || schemaConfig != null || onSubmit != null;
    const isLayoutOnly = isInsideForm && !hasOwnState && !standalone && typeof children !== "function";
    if (isLayoutOnly) {
        return (
            <div
                className={classNames(styles.formRoot, FORM_ROOT_CLASS, className)}
                style={{ gridTemplateColumns }}
                data-form-root="true"
            >
                {children}
            </div>
        );
    }
    return <FormStateful {...props} ref={ref} />;
}) as <TFieldValues extends FieldValues = FieldValues>(
    props: FormProps<TFieldValues> & { ref?: React.Ref<FormHandle<TFieldValues>> },
) => React.ReactElement | null;

/** Корневой компонент формы: единая реализация для обоих режимов (со state и layout-only). */
const FormRoot = FormWithState;
/**
 * Пропсы компонента {@link FormSection}.
 */
interface FormSectionProps {
    /**
     * Заголовок секции (опционально).
     */
    title?: string; // To do: add jsx, like <h2>{title}</h2> or <CustomTitle>{title}</CustomTitle>
    /**
     * Дочерние элементы — поля ввода, строки (`Form.Row`) или другие компоненты.
     */
    children: React.ReactNode;
    /**
     * Сколько колонок макросетки (`Form`) должна занимать секция.
     *
     * @default 1
     */
    span?: 1 | 2 | 3 | 4;
    /**
     * Добавляет нижнюю границу (визуальное разделение между секциями).
     *
     * @default false
     */
    bordered?: boolean;
    /**
     * Дополнительный CSS-класс для корня и/или контента секции.
     * Строка применяется к корню; объект — к root и content по ключам.
     */
    className?: ClassNameMap<"root" | "content">;
}

/**
 * Логический блок формы: опциональный заголовок и контент в колонке макросетки {@link Form}.
 *
 * @example
 * ```tsx
 * <Form.Section title="Реквизиты" span={2} bordered>
 *   <Form.Row columns={2}>…</Form.Row>
 * </Form.Section>
 * ```
 */
const FormSection: React.FC<FormSectionProps> = ({ title, children, span = 1, bordered = false, className }) => {
    const classes = parseClassName<"root" | "content">(className, "root");
    return (
        <div
            className={classNames(styles.formSectionRoot, classes.root, {
                [styles.bordered]: bordered,
            })}
            style={{
                gridColumn: `span ${span}`,
            }}
        >
            {title != null && <div className={styles.title}>{title}</div>}
            <div className={classNames(styles.content, "section_content", classes.content)}>{children}</div>
        </div>
    );
};

/**
 * Пропсы компонента {@link FormRow}.
 */
interface FormRowProps {
    /**
     * Дополнительный CSS-класс для корневого элемента строки.
     */
    className?: string;
    /**
     * Дочерние элементы — обычно поля ввода (`Input`, `Select` и т.д.).
     */
    children: React.ReactNode;
    /**
     * Спецификация колонок для внутренней сетки строки.
     *
     * Управляет шириной полей внутри строки. Поддерживает как равномерные,
     * так и неравномерные распределения.
     *
     * @default "auto" (поля распределяются равномерно через flex)
     * @see {@link ColumnSpec}
     */
    columns?: ColumnSpec;
    /**
     * Вертикальное выравнивание элементов в строке (CSS `align-items`).
     *
     * @default "start"
     */
    align?: "start" | "center" | "end";
}

/**
 * Горизонтальная группа полей внутри {@link FormSection} (внутренняя CSS Grid).
 *
 * @example
 * ```tsx
 * <Form.Row columns={2}>
 *   <Input label="Код" />
 *   <Input label="Имя" />
 * </Form.Row>
 * ```
 */
const FormRow: React.FC<FormRowProps> = ({ className, children, columns = "auto", align = "start" }) => {
    const gridTemplateColumns = getColumnTemplate(columns);
    return (
        <div
            className={classNames(styles.formRowRoot, className)}
            style={{
                display: "grid",
                gridTemplateColumns,
                alignItems: align,
            }}
        >
            {children}
        </div>
    );
};
/**
 * Корневой компонент формы с поддержкой CSS Grid.
 *
 * Предназначен для создания структурированных форм с гибким расположением секций.
 * Используется вместе с подкомпонентами `Form.Section` и `Form.Row`.
 *
 * При форме с состоянием поддерживается ref: через {@link FormHandle} можно вызывать
 * submit снаружи (например из футера модалки).
 *
 * @example
 * ```tsx
 * <Form columns={2}>
 *   <Form.Section title="Деталь" span={1}>
 *     <Input label="Код" />
 *   </Form.Section>
 *   <Form.Section title="Операция" span={1}>
 *     <Input label="Наименование" />
 *   </Form.Section>
 * </Form>
 * ```
 */
/**
 * Compound-компонент {@link Form}: вызов с дженериком и ref плюс статические `Section` / `Row` / `Field`.
 *
 * @template TFieldValues тип значений формы при вызове `<Form>`
 */
interface FormComponent {
    /**
     * Корневая форма с опциональным ref {@link FormHandle}.
     * @template TFieldValues тип значений полей
     */
    <TFieldValues extends FieldValues = FieldValues>(
        props: FormProps<TFieldValues> & { ref?: React.Ref<FormHandle<TFieldValues>> },
    ): React.ReactElement | null;
    /** @see {@link FormSection} */
    Section: typeof FormSection;
    /** @see {@link FormRow} */
    Row: typeof FormRow;
    /** @see {@link FormField} */
    Field: typeof FormField;
}
/** Compound-реализация `Form` со статическими подкомпонентами `Section`, `Row` и `Field`. */
const FormWithSubcomponents = Object.assign(FormRoot, {
    /**
     * Логический блок формы с необязательным заголовком.
     *
     * Представляет собой вертикальную группу полей. Может занимать несколько колонок
     * макросетки формы через пропс `span`.
     *
     * Внутри может содержать как отдельные поля, так и строки (`Form.Row`).
     *
     * @example
     * ```tsx
     * <Form.Section title="Оборудование" span={2} bordered>
     *   <Input label="Группа" />
     *   <Form.Row columns={2}>
     *     <Input label="Код" />
     *     <Input label="Наименование" />
     *   </Form.Row>
     * </Form.Section>
     * ```
     */
    Section: FormSection,
    /**
     * Горизонтальная строка полей внутри секции.
     *
     * Используется для размещения нескольких полей в одну строку.
     * Поддерживает как равномерное (`columns={2}`), так и неравномерное
     * (`columns="1fr 2fr"`) распределение ширины.
     *
     * Использует технику с `margin/padding` вместо `gap`, чтобы обеспечить
     * выравнивание колонок между строками с разным количеством полей.
     *
     * @example
     * ```tsx
     * <Form.Row columns={2}>
     *   <Input label="Код" />
     *   <Input label="Наименование" />
     * </Form.Row>
     *
     * <Form.Row columns="120px 1fr">
     *   <Input label="№" />
     *   <Input label="Описание" />
     * </Form.Row>
     * ```
     */
    Row: FormRow,
    /**
     * Поле формы с привязкой к состоянию (react-hook-form).
     * Использовать только внутри Form с initialValues/onSubmit/zodSchema.
     * Children — render-функция: получает { field, fieldState }, возвращает разметку поля.
     *
     * @example
     * ```tsx
     * <Form.Field name="code">
     *   {({ field, fieldState }) => (
     *     <Input label="Код" {...field} value={field.value ?? ""} error={Boolean(fieldState.error)} />
     *   )}
     * </Form.Field>
     * ```
     */
    Field: FormField,
}) as FormComponent;
export { FormWithSubcomponents as Form };
export type { FormProps };
